import type { SupportContact, SupportContactChannel } from '@/shared/constants';
import { Mail, Phone, type LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';

function WhatsAppIcon({ className }: LucideProps) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='currentColor'
      aria-hidden='true'
      className={className}
    >
      <path d='M12.04 2C6.6 2 2.2 6.4 2.2 11.84c0 1.94.55 3.75 1.5 5.29L2 22l4.99-1.63a9.8 9.8 0 0 0 5.05 1.39c5.44 0 9.84-4.4 9.84-9.84S17.48 2 12.04 2Zm0 17.9c-1.6 0-3.1-.47-4.35-1.28l-.31-.2-3.1 1.01 1.02-3-.2-.32a7.94 7.94 0 0 1-1.23-4.27c0-4.42 3.6-8.02 8.02-8.02s8.02 3.6 8.02 8.02-3.6 8.06-8.02 8.06Zm4.52-5.9c-.25-.13-1.46-.72-1.69-.8-.23-.09-.4-.13-.56.12-.17.25-.65.8-.8.96-.14.17-.29.19-.53.06a6.5 6.5 0 0 1-1.93-1.19 7.2 7.2 0 0 1-1.33-1.66c-.14-.25-.02-.39.1-.52.12-.13.41-.48.53-.65.12-.17.06-.31 0-.44-.06-.13-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.42h-.48c-.16 0-.43.06-.65.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.57.13.17 1.75 2.78 4.25 3.79 2.5 1 2.5.67 2.95.63.45-.04 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18-.06-.1-.23-.17-.48-.29Z' />
    </svg>
  );
}

const CHANNEL_ICONS: Record<
  SupportContactChannel,
  ComponentType<LucideProps>
> = {
  phone: Phone,
  whatsapp: WhatsAppIcon,
  email: Mail,
};

interface AuthSupportPanelProps {
  contacts: SupportContact[];
  className?: string;
}

export function AuthSupportPanel({
  contacts,
  className = '',
}: AuthSupportPanelProps) {
  return (
    <section
      className={`flex flex-col justify-end gap-5 px-[clamp(20px,4vw,72px)] pt-[clamp(24px,3vw,40px)] pb-[clamp(28px,4vw,64px)] text-white ${className}`}
    >
      <div>
        <h2 className='mb-1.5 text-[clamp(20px,2vw,30px)] font-bold leading-[1.2]'>
          Central de Atendimento ao Corretor
        </h2>
        <p className='max-w-[460px] text-[clamp(13px,1.05vw,16px)] leading-[1.45] text-white/90'>
          Fale com a gente em nossos canais exclusivos para suporte técnico e
          atendimento operacional:
        </p>
      </div>

      <div className='flex flex-wrap items-end gap-[clamp(20px,4vw,56px)]'>
        <ul className='flex flex-col gap-3.5'>
          {contacts.map(contact => {
            const Icon = CHANNEL_ICONS[contact.channel];
            const isExternal = contact.href.startsWith('http');

            return (
              <li key={contact.channel}>
                <a
                  href={contact.href}
                  {...(isExternal && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                  className='flex items-center gap-3.5 text-[clamp(14px,1.15vw,18px)] font-semibold text-white transition-colors hover:text-[#bdf0d3]'
                >
                  <Icon className='h-[22px] w-[22px] shrink-0' />
                  {contact.label}
                </a>
              </li>
            );
          })}
        </ul>

        <p className='text-[clamp(13px,1.05vw,16px)] leading-[1.45] text-white/95'>
          Disponível de segunda a sexta,
          <br />
          das 08h às 18h, <em>(exceto feriados)</em>.
        </p>
      </div>
    </section>
  );
}
